import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Box,
  Stack,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Add, FilterList } from "@mui/icons-material";
import PayrollEntryListTable from "./PayrollEntryListTable";
import SearchFilters from "./SearchFilters";
import { GetReadOnlyInputProps } from "../../HelperFunctions/HelperFunctions";
import {
  VIEW_TRANSACTION_OPTIONS as COMMON_TRANSACTION_OPTIONS,
  getDateRangeForOption,
} from "../../HelperFunctions/ViewTransaction/dateRangeOptions";
import CustomeDateRange from "../../Components/CustomeDateRange";
import CustomPagination from "../../Components/CustomPagination";

import PayPeriodSetup from "./PayPeriodSetup/payPeriodSetup";
import PayrollItemTypeMapping from "./../../Components/PayrollItemTypeMapping";
import FileUploadBox from "../../Components/FileUploadBox";
import {
  uploadPayrollFile,
} from "../../APIs/ParollEntryApi";
import {
  getViewGrid,
  deletePayrollEntry,
  getDefaultDateRange,
  getCorporations,
  getPayrollVendorTypes,
  getVendorPref,
  downloadSampleTemplate,
  getProfitCenters,
} from "../../api";

import SampleTemplate from "../../files/Payroll Entry Sample Format.xlsx";
import PayrollsPopup from "../../Components/PayrollPopup";
import { buildViewGridPayload } from "../../Payloads/EntryPayload";
import {
  STATUS_OPERATOR_MAP,
  STATUS_VALUE_MAP,
  DATE_RANGE_MAP,
} from "../../Enums/Enum";
import { useAlerts, usePermissions } from "../../Hooks";
import { SnackbarProvider } from "notistack";
import { Loader } from "../../Components/Loader";
import { Dropdown } from "../../HelperFunctions/PayrollDropdown";

const sortSeparator = {
  label: "-----------------",
  value: "-2-sort",
  isSeparator: true,
  optionType: "separator",
};

const VIEW_TRANSACTION_OPTIONS = [
  ...COMMON_TRANSACTION_OPTIONS,
  sortSeparator,
  { label: "Latest Entries", date: "Latest Entries", value: "sort-101", optionType: "sort", sortBy: 101 },
  { label: "Latest Created", date: "Latest Created", value: "sort-102", optionType: "sort", sortBy: 102 },
  { label: "Latest Modified", date: "Latest Modified", value: "sort-103", optionType: "sort", sortBy: 103 },
];

const PayrollEntry = () => {
  const [corporations, setCorporations] = useState([
    { corpName: "--ALL--", corpValue: "000000000000000000000000000000000000" },
  ]);
  const { corpId, menuId } = useParams();

  const defaultDateRange =
    VIEW_TRANSACTION_OPTIONS.find((opt) => opt.value === "3" || opt.label === "Range") ||
    VIEW_TRANSACTION_OPTIONS[0];

  const [editRow, setEditRow] = useState(null);
  const [itemMappingRow, setItemMappingRow] = useState(null);
  const [sortBy, setSortBy] = useState(107);
  const [status, setStatus] = useState("--All--");
  const [showSearch, setShowSearch] = useState(false);
  const [selectedCorp, setSelectedCorp] = useState({
    corpName: "--ALL--",
    corpValue: "000000000000000000000000000000000000",
  });
  const [viewTransaction, setViewTransaction] = useState(defaultDateRange);
  const [viewTransactionInput, setViewTransactionInput] = useState(
    defaultDateRange?.label || "Range",
  );
  const [viewTransactionOpen, setViewTransactionOpen] = useState(false);
  const [highlightedViewTransaction, setHighlightedViewTransaction] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [highlightedStatus, setHighlightedStatus] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [sortOrder, setSortOrder] = useState(true);
  const [rows, setRows] = useState([]);
  const [gridLoading, setGridLoading] = useState(true);
  const [corpLoading, setCorpLoading] = useState(true);
  const [dateRangeLoading, setDateRangeLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [payrollEmail, setPayrollEmail] = useState("");
  const [isAddPayRoll, setIsAddPayRoll] = useState(false);
  const [showProfitCenter, setShowProfitCenter] = useState(false);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [selectedVendorType, setSelectedVendorType] = useState("");
  const [vendorPrefVendorId, setVendorPrefVendorId] = useState(null);
  const [profitCenters, setProfitCenters] = useState([]);
  const [selectedPC, setSelectedPC] = useState("");
  const [pcLoading, setPcLoading] = useState(false);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const snackbarRef = useRef(null);

  const {
    popup,
    showDeleteConfirm,
    hidePopup,
  } = useAlerts();
  const showErrorSnackbar = (msg) => {
    snackbarRef.current?.enqueueSnackbar(msg, {
      variant: "error",
    });
  };

  const showSuccessSnackbar = (msg) => {
    snackbarRef.current?.enqueueSnackbar(msg, {
      variant: "success",
    });
  };
  const { permissions, loading: permsLoading } = usePermissions(menuId);
  const isGridLoading =
    permsLoading || corpLoading || dateRangeLoading || gridLoading;
  const loading = isGridLoading || uploadLoading;

  const columns = useMemo(() => [
    { name: "", dataKey: "isEntryUsed", width: "5%" },
    { name: "Corporation Name", dataKey: "corporationName", width: "14%" },
    ...(showProfitCenter ? [{ name: "Profit Center", dataKey: "profitCenter", width: "12%" }] : []),
    { name: "Pay Frequency", dataKey: "payFrequency", width: "10%" },
    { name: "Pay Period", dataKey: "payPeriod", width: "13%" },
    { name: "Wage Cost Name", dataKey: "WageCostName", width: "14%" },
    { name: "Account Number", dataKey: "AccountNumber", width: "14%" },
    { name: "No.of Employees", dataKey: "employeeCount", width: "10%" },
    { name: "Total Payment", dataKey: "totalPayment", width: "10%" },
    { name: "Entry Type", dataKey: "entryType", width: "10%" },
    { name: "Status", dataKey: "status", width: "12%" },
  ], [showProfitCenter]);

  const handleTransactions = (event, newValue) => {
    if (!newValue || newValue.optionType === "separator" || newValue.isSeparator) {
      setViewTransaction(defaultDateRange);
      setViewTransactionInput(defaultDateRange?.label || "Range");
      setFromDate(null);
      setToDate(null);
      setPage(0);
      setRefreshKey((prev) => prev + 1);
      return;
    }

    if (newValue.optionType === "sort") {
      setSortBy(Number(newValue.sortBy) || 101);
      setViewTransaction(newValue);
      setViewTransactionInput(newValue.label || newValue.date || "");
      setFromDate(null);
      setToDate(null);
      setPage(0);
      return;
    }

    setViewTransaction(newValue);
    setViewTransactionInput(newValue.label || newValue.date || "");
    const range = getDateRangeForOption(newValue.value);
    if (range && range.fromDate && range.toDate) {
      setFromDate(range.fromDate);
      setToDate(range.toDate);
    } else {
      setFromDate(null);
      setToDate(null);
    }
    setSortBy(107); // Reset sortBy to a default that shows pagination
    setPage(0);
  };


  const selectedMatchedVendor = useMemo(() => {
    if (!selectedVendorType) return null;
    const invalidValues = ["--Select Payroll Vendor--", "--Select Vendor--", "--Select--", ""];
    if (invalidValues.includes(String(selectedVendorType).trim())) return null;
    return (
      vendorTypes.find(
        (v) =>
          v.Value &&
          v.Value === selectedVendorType &&
          (v.OrderID ?? v.OrderId) != null &&
          (v.OrderID ?? v.OrderId) !== ""
      ) || null
    );
  }, [selectedVendorType, vendorTypes]);

  const isVendorValid = Boolean(selectedMatchedVendor);

  const onUploadClick = () => {
    if (!selectedCorp?.corpValue) {
      showErrorSnackbar(
        "Please select a corporation before uploading the file.",
      );
      return false;
    }

    if (selectedCorp.corpValue === "000000000000000000000000000000000000") {
      showErrorSnackbar("Please select a specific corporation before uploading a file.");
      return false;
    }

    if (!isVendorValid || !selectedMatchedVendor) {
      showErrorSnackbar("Please select a Payroll Vendor before uploading the file.");
      return false;
    }
    return true;
  };

  const handleFileUpload = async (file) => {
    if (!selectedCorp?.corpValue) {
      showErrorSnackbar(
        "Please select a corporation before uploading the file.",
      );
      return;
    }

    if (selectedCorp.corpValue === "000000000000000000000000000000000000") {
      showErrorSnackbar("Please select a specific corporation before uploading a file.");
      return;
    }

    const matchedVendor = selectedMatchedVendor || vendorTypes.find(
      (v) =>
        v.Value &&
        v.Value === selectedVendorType &&
        (v.OrderID ?? v.OrderId) != null &&
        (v.OrderID ?? v.OrderId) !== ""
    );
    const vendorId = matchedVendor?.OrderID ?? matchedVendor?.OrderId ?? "";

    if (!isVendorValid || !matchedVendor || !vendorId) {
      showErrorSnackbar("Please select a Payroll Vendor before uploading the file.");
      return;
    }

    // console.log("Uploading file:", file);
    setUploadLoading(true);
    try {
      const pcIdToUse = selectedPC || "";

      await uploadPayrollFile(file, selectedCorp?.corpValue, vendorId, pcIdToUse || null).then((uploadResponse) => {
        if (uploadResponse?.StatusCode === 200) {
          showSuccessSnackbar("File uploaded successfully.");
          setRefreshKey((prev) => prev + 1);
        } else {
          showErrorSnackbar("File Imported Failed.");
          setRefreshKey((prev) => prev + 1);
        }
      });
      // await cacheImportedPayroll(selectedCorp?.corpValue); 
    } catch (err) {
      console.error("Upload Error:", err);
      showErrorSnackbar(
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to upload file.",
      );
    } finally {
      setUploadLoading(false);
      setFileUploadKey((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (!editRow) return;

    window.parent.postMessage("HideMenu", "*");

    return () => {
      window.parent.postMessage("ShowMenu", "*");
    };
  }, [editRow]);

  const handleDelete = async (row) => {
    if (row.raw?.ReconStatus === 6 || row.raw?.ReconStatus === 19 || row.raw?.ClearedDate) {
      showErrorSnackbar("This item is referenced and cannot be deleted");
      return;
    }

    showDeleteConfirm(
      "Are you sure you want to delete this payroll entry?",
      async () => {
        setActionLoading(true);
        try {
          const response = await deletePayrollEntry(row.id);
          if (response?.StatusCode === 200) {
            showSuccessSnackbar("Payroll entry deleted successfully.");
            setRefreshKey((prev) => prev + 1);
          } else {
            showErrorSnackbar(response?.Status || "Failed to delete payroll entry.");
          }
        } catch (error) {
          showErrorSnackbar(error.message || "Failed to delete payroll entry.");
        } finally {
          setActionLoading(false);
        }
      },
    );
  };

  useEffect(() => {
    if (permsLoading) return;
    const fetchCorporations = async () => {
      try {
        const data = await getCorporations();
        const formatted = (data || []).map((corp) => ({
          corpName: corp.Name ?? corp.CorporationName ?? "",
          corpValue: corp.ID ?? corp.CorpID ?? corp.Id ?? "",
        }));
        const defaultOption = { corpName: "--ALL--", corpValue: "000000000000000000000000000000000000" };
        setCorporations([defaultOption, ...formatted]);

        const urlCorpID = corpId;

        if (urlCorpID) {
          if (urlCorpID === "000000000000000000000000000000000000") {
            setSelectedCorp(defaultOption);
          } else {
            const matched = formatted.find(
              (c) => c.corpValue.toLowerCase() === urlCorpID.toLowerCase(),
            );
            if (matched) {
              setSelectedCorp(matched);
              setPage(0);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching corporations:", error);
      } finally {
        setCorpLoading(false);
      }
    };

    fetchCorporations();
  }, [corpId, permsLoading]);


  useEffect(() => {
    if (permsLoading) return;
    const fetchVendorTypes = async () => {
      try {
        const response = await getPayrollVendorTypes();
        const options = (response?.LoadPayrollVendorTypes || []).map((v) => ({
          Value: v.Value,
          OrderID: v.OrderID,
          ID: v.ID,
          label: v.Value,
          value: v.Value,
        }));
        setVendorTypes([
          { label: "--Select Payroll Vendor--", value: "", Value: "", OrderID: "", ID: "" },
          ...options,
        ]);
      } catch (error) {
        console.error("Error fetching vendor types:", error);
      }
    };
    fetchVendorTypes();
  }, [permsLoading]);

  const vendorTypesRef = useRef(vendorTypes);
  useEffect(() => {
    vendorTypesRef.current = vendorTypes;
  }, [vendorTypes]);

  const fetchVendorPreference = useCallback(async (corpIdToUse, pcIdToUse) => {
    if (!corpIdToUse || corpIdToUse === "000000000000000000000000000000000000") {
      setSelectedVendorType("");
      setPayrollEmail("");
      setVendorPrefVendorId(null);
      return;
    }
    try {
      const pref = await getVendorPref(corpIdToUse, pcIdToUse || null);
      const email = pref?.PayrollEmail || pref?.Email || pref?.email || "";
      setPayrollEmail(email);

      const prefVendorId = pref?.PayrollVendorID || null;
      setVendorPrefVendorId(prefVendorId);
      const currentVendorTypes = vendorTypesRef.current;
      if (prefVendorId && currentVendorTypes && currentVendorTypes.length > 0) {
        const matched = currentVendorTypes.find(
          (v) => String(v.ID) === String(prefVendorId) && (v.OrderID ?? v.OrderId)
        );
        setSelectedVendorType(matched ? matched.Value : "");
      } else if (!prefVendorId) {
        setSelectedVendorType("");
      }
    } catch (error) {
      console.error("Error fetching vendor preference:", error);
      setSelectedVendorType("");
      setVendorPrefVendorId(null);
      const errData = error?.response?.data;
      const fallbackEmail = errData?.PayrollEmail || errData?.Email || errData?.email || "";
      setPayrollEmail(fallbackEmail);
    }
  }, []);

  useEffect(() => {
    if (vendorPrefVendorId && vendorTypes.length > 0) {
      const matched = vendorTypes.find(
        (v) => String(v.ID) === String(vendorPrefVendorId) && (v.OrderID ?? v.OrderId)
      );
      if (matched) {
        setSelectedVendorType(matched.Value);
      } else {
        setSelectedVendorType("");
      }
    }
  }, [vendorPrefVendorId, vendorTypes]);

  useEffect(() => {
    if (permsLoading) return;
    const corpIdToUse = selectedCorp?.corpValue;
    if (!corpIdToUse || corpIdToUse === "000000000000000000000000000000000000") {
      setProfitCenters([]);
      setSelectedPC("");
      setSelectedVendorType("");
      setPayrollEmail("");
      setVendorPrefVendorId(null);
      return;
    }

    let isCurrent = true;
    const loadPCAndPreference = async () => {
      setPcLoading(true);
      try {
        const pcList = await getProfitCenters(corpIdToUse);
        if (!isCurrent) return;
        const pcOptions = (pcList || []).map((pc) => ({
          label: pc.label || pc.Name || pc.ProfitCenterName,
          value: pc.value || pc.ID || pc.ProfitCenterId || pc.PCID,
        }));
        if (pcOptions.length > 0) {
          const options = [{ label: "--Select--", value: "" }, ...pcOptions];
          setProfitCenters(options);
          const initialPC = pcOptions[0].value;
          setSelectedPC(initialPC || "");

          if (isCurrent) {
            await fetchVendorPreference(corpIdToUse, initialPC);
          }
        } else {
          setProfitCenters([]);
          setSelectedPC("");
          if (isCurrent) {
            await fetchVendorPreference(corpIdToUse, null);
          }
        }
      } catch (error) {
        if (!isCurrent) return;
        console.error("Error fetching profit centers:", error);
        setProfitCenters([]);
        setSelectedPC("");
        await fetchVendorPreference(corpIdToUse, null);
      } finally {
        if (isCurrent) {
          setPcLoading(false);
        }
      }
    };

    loadPCAndPreference();

    return () => {
      isCurrent = false;
    };
  }, [selectedCorp, permsLoading, fetchVendorPreference]);

  useEffect(() => {
    if (permsLoading) return;
    const fetchDateRangePref = async () => {
      try {
        const response = await getDefaultDateRange();
        const rangeId = String(response);

        const selectedOption =
          VIEW_TRANSACTION_OPTIONS.find((opt) => opt.value === rangeId) ||
          VIEW_TRANSACTION_OPTIONS.find((opt) => opt.label === DATE_RANGE_MAP[response]) ||
          VIEW_TRANSACTION_OPTIONS.find((opt) => opt.label === response);

        if (selectedOption && selectedOption.optionType !== "separator" && !selectedOption.isSeparator) {
          setViewTransaction(selectedOption);
          setViewTransactionInput(selectedOption.label || "");
          const range = getDateRangeForOption(selectedOption.value);
          if (range && range.fromDate && range.toDate) {
            setFromDate(range.fromDate);
            setToDate(range.toDate);
          } else {
            setFromDate(null);
            setToDate(null);
          }
          setPage(0);
        }
      } catch (error) {
        console.error("Error fetching date range preference:", error);
      } finally {
        setDateRangeLoading(false);
      }
    };
    fetchDateRangePref();
  }, [permsLoading]);

  useEffect(() => {
    if (permsLoading || corpLoading || dateRangeLoading) return;

    let isCancelled = false;
    setGridLoading(true);

    const fetchGridData = async () => {
      try {
        const payload = buildViewGridPayload(
          selectedCorp,
          fromDate,
          toDate,
          sortOrder,
          page,
          sortBy,
          searchFilters,
          status,
          STATUS_OPERATOR_MAP,
          STATUS_VALUE_MAP,
        );

        const response = await getViewGrid(payload);

        if (isCancelled) return;

        const data = response?.GridDetails || [];
        const hasProfitCenter = Array.isArray(data) && data.some(
          (item) =>
            (item.PCID !== null && item.PCID !== undefined && String(item.PCID).trim() !== "") ||
            (item.PCName !== null && item.PCName !== undefined && String(item.PCName).trim() !== "")
        );
        setShowProfitCenter(hasProfitCenter);

        const totalRecords =
          response?.TotalRecords ||
          (data.length > 0 ? data[0].TotalRecords : 0) ||
          0;
        const pageSize =
          response?.MaxRows || (data.length > 0 ? data[0].MaxRows : 40) || 40;
        setTotalPages(Math.ceil(totalRecords / pageSize) || 1);
        const formattedRows = (data || []).map((item, index) => ({
          id: item.PayrollID ?? item.RowNum ?? index + 1,
          corporationId: item.CorporationID ?? "",
          corporationName: item.CorporationName ?? "",
          profitCenter: item.PCName ?? "",
          profitCenterNames: item.PCNames ?? item.pcNames ?? "",
          payFrequency: item.PayFrequency ?? "",
          payPeriod: item.PayPeriod ?? "",
          WageCostName: item.WageCostName ?? "",
          AccountNumber: item.AccountNumber ?? "",
          employeeCount: item.EmpCount ?? 0,
          totalPayment: item.Amount ?? 0,
          status: item.IsActive ?? "",
          raw: item,
          entryType: (() => {
            switch (item.ImportPayrollType) {
              case 0:
                return "AUTO";
              case 1:
                return "MANUAL";
              case 2:
                return "Import";
              default:
                return "";
            }
          })(),
        }));

        setRows(formattedRows);
      } catch (err) {
        if (!isCancelled) {
          console.error("Error loading payroll entries:", err);
          showErrorSnackbar("Failed to load payroll entries.");
          setRows([]);
          setShowProfitCenter(false);
        }
      } finally {
        if (!isCancelled) {
          setGridLoading(false);
        }
      }
    };

    fetchGridData();

    return () => {
      isCancelled = true;
    };
  }, [
    selectedCorp,
    fromDate,
    toDate,
    searchFilters,
    status,
    sortOrder,
    refreshKey,
    page,
    sortBy,
    viewTransaction,
    corpId,
    permsLoading,
    corpLoading,
    dateRangeLoading,
  ]);

  const handleSampleDownload = async () => {
    const currentVendor = selectedMatchedVendor;
    const orderId = currentVendor?.OrderID ?? currentVendor?.OrderId;

    if (!orderId || !isVendorValid) {
      showErrorSnackbar("Please select a valid Payroll Vendor before downloading template");
      return;
    }

    const processTemplateResponse = (response, customFileName) => {
      const fileData = response?.File?.Files?.[0] || response?.Files?.[0] || (Array.isArray(response) ? response[0] : null);
      const base64Content = fileData?.FileContent || response?.FileContent;
      if (base64Content) {
        const serverFileName = fileData?.FileName || "";
        const dotIndex = serverFileName.lastIndexOf(".");
        const ext = dotIndex !== -1 ? serverFileName.substring(dotIndex) : "";
        const fileName = customFileName || `${selectedVendorType} Sample Format${ext}`;
        const byteCharacters = atob(base64Content);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArray[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return true;
      }
      return false;
    };


    let downloadSuccessful = false;


    // 1. If .xlsx is not found or failed, try .pdf template
    try {
      const pdfResponse = await downloadSampleTemplate(`${orderId}.pdf`);
      // console.log("PDF Template API Response:", pdfResponse);
      if (processTemplateResponse(pdfResponse, `${selectedVendorType} Sample Format.pdf`)) {
        downloadSuccessful = true;
        return;
      }
    } catch (err) {
      console.warn("Could not download .pdf template from API:", err);
    }
    // 2. Try downloading .xlsx template first
    try {
      const xlsxResponse = await downloadSampleTemplate(`${orderId}.xlsx`);
      // console.log("XLSX Template API Response:", xlsxResponse);
      if (processTemplateResponse(xlsxResponse, `${selectedVendorType} Sample Format.xlsx`)) {
        downloadSuccessful = true;
        return;
      }
    } catch (err) {
      console.warn("Could not download .xlsx template from API, attempting .pdf fallback:", err);
    }

    // 3. If no file is downloaded, throw the error
    if (!downloadSuccessful) {
      showErrorSnackbar(`${selectedVendorType} Sample Format is not Configured`);
    }
  };


  return (
    <SnackbarProvider
      ref={snackbarRef}
      maxSnack={1}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      autoHideDuration={2000}
      style={{ zIndex: 99999 }}
    >
      <>

        {actionLoading && (<Loader loading={actionLoading} />)}
        <Box
          height={"100%"}
          // border={"1px solid #f5f5f5"}
          display={"flex"}
          flexDirection={"column"}
          gap={0}
          padding={0}
        >
          <FileUploadBox
            key={fileUploadKey}
            onFileSelect={(file) => {
              // Validation is now handled by onUploadClick, which prevents the file dialog from opening.
              if (!permissions.canCreate) {
                showErrorSnackbar("You don't have permission to upload payroll files.");
                return;
              }
              if (!onUploadClick()) {
                return;
              }
              handleFileUpload(file);
            }}
            disableDragAndDrop={true}
            onUploadClick={onUploadClick}
            sampleFile={SampleTemplate}
            sampleFileName="Payroll Entry Sample Format.xlsx"
            onSampleDownload={handleSampleDownload}
            allowedExtensions={[".csv", ".xlsx", ".xls", ".pdf"]}
            email={payrollEmail}
            disabled={
              pcLoading ||
              !selectedCorp?.corpValue ||
              selectedCorp.corpValue === "000000000000000000000000000000000000" ||
              !isVendorValid
            }

            extraLeftContent={
              <Stack direction="row" spacing={1} alignItems="center">
                {profitCenters && profitCenters.length > 0 && profitCenters.some((pc) => pc.value !== "") && (
                  <Box sx={{ minWidth: 150, width: 150 }}>
                    <Dropdown
                      name="profitCenter"
                      value={
                        selectedPC !== ""
                          ? (profitCenters.find((pc) => String(pc.value) === String(selectedPC)) || profitCenters.find((pc) => pc.value !== "") || null)
                          : (profitCenters.find((pc) => pc.value === "") || null)
                      }
                      options={profitCenters}
                      onChange={(name, option) => {
                        const newPC = option?.value || "";
                        setSelectedPC(newPC);
                        const corpIdToUse = selectedCorp?.corpValue;
                        if (corpIdToUse && corpIdToUse !== "000000000000000000000000000000000000") {
                          fetchVendorPreference(corpIdToUse, newPC);
                        }
                      }}
                      getOptionLabel={(o) => o?.label || o?.Name || ""}
                      isEqual={(o, v) => String(o?.value ?? "") === String(v?.value ?? "")}
                      sx={{
                        "& .MuiFilledInput-root": {
                          height: 28,
                          minHeight: 28,
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: 1,
                          "&:before, &:after": { display: "none" },
                          "&:hover:not(.Mui-disabled):before": { display: "none" },
                          "&.Mui-focused": { backgroundColor: "white" }
                        },
                        "& .MuiAutocomplete-input": {
                          fontSize: "12px",
                          padding: "0 8px !important",
                        }
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ minWidth: 180, width: 180 }}>
                  <Dropdown
                    name="vendorType"
                    value={
                      vendorTypes.find((v) => v.Value && v.Value === selectedVendorType) ||
                      vendorTypes[0] ||
                      null
                    }
                    options={vendorTypes}
                    onChange={(name, option) => {
                      const val = option?.Value || option?.value || "";
                      const invalidValues = ["--Select Payroll Vendor--", "--Select Vendor--", "--Select--"];
                      setSelectedVendorType(invalidValues.includes(val) ? "" : val);
                    }}
                    getOptionLabel={(o) => o?.Value || o?.label || "--Select Payroll Vendor--"}
                    isEqual={(o, v) => (o?.Value ?? o?.value ?? "") === (v?.Value ?? v?.value ?? "")}
                    sx={{
                      "& .MuiFilledInput-root": {
                        height: 28,
                        minHeight: 28,
                        backgroundColor: "white",
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        "&:before, &:after": { display: "none" },
                        "&:hover:not(.Mui-disabled):before": { display: "none" },
                        "&.Mui-focused": { backgroundColor: "white" }
                      },
                      "& .MuiAutocomplete-input": {
                        fontSize: "12px",
                        padding: "0 8px !important",
                      }
                    }}
                  />
                </Box>
              </Stack>
            }
          />
          {/* <div>In Progress: {inProgressCount}</div> */}

          <Stack gap={1} padding={0} flexGrow={1} sx={{ overflow: "hidden" }}>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Stack direction={"row"} gap={1}>
                <Autocomplete
                  options={VIEW_TRANSACTION_OPTIONS}
                  filterOptions={(options) => options}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || option?.date || "")}
                  isOptionEqualToValue={(option, value) =>
                    Boolean(option?.value && value?.value && option.value === value.value) ||
                    Boolean(option?.label && value?.label && option.label === value.label) ||
                    Boolean(option?.sortBy && value?.sortBy && option.sortBy === value.sortBy)
                  }
                  getOptionDisabled={(option) => option.optionType === "separator" || Boolean(option.isSeparator)}
                  renderOption={(props, option, state) => {
                    const { key, ...restProps } = props;
                    const uniqueKey = option?.value || option?.sortBy || `${option?.label || 'opt'}-${state?.index ?? ''}`;
                    const isSep = option?.optionType === "separator" || Boolean(option?.isSeparator);
                    const label = typeof option === 'string' ? option : option?.label || option?.date || "";
                    return (
                      <li
                        key={uniqueKey}
                        {...restProps}
                        title={label}
                        style={{
                          ...restProps.style,
                          ...(isSep ? { pointerEvents: "none", color: "#999", fontSize: "11px", minHeight: "20px" } : {})
                        }}
                      >
                        <span title={label}>{label}</span>
                      </li>
                    );
                  }}
                  size="small"
                  disableClearable
                  autoHighlight
                  selectOnFocus
                  open={viewTransactionOpen}
                  onOpen={() => setViewTransactionOpen(true)}
                  onClose={() => setViewTransactionOpen(false)}
                  value={viewTransaction}
                  inputValue={viewTransactionInput}
                  onHighlightChange={(event, option) => setHighlightedViewTransaction(option)}
                  ListboxProps={{
                    sx: {
                      maxHeight: 250,
                      "& .MuiAutocomplete-option": { fontSize: 11 },
                      "& .MuiAutocomplete-option[aria-selected='true']": {
                        backgroundColor: "#2f5d8a !important",
                        color: "white",
                      },
                    },
                  }}
                  onChange={handleTransactions}
                  sx={{ minWidth: "200px" }}
                  renderInput={(params) => {
                    const labelText = params.inputProps?.value || viewTransaction?.label || viewTransaction?.date || (typeof viewTransaction === 'string' ? viewTransaction : "") || "";
                    return (
                      <span title={labelText || undefined} style={{ width: "100%", display: "inline-block" }}>
                        <TextField
                          {...params}
                          title={labelText || undefined}
                          inputProps={{
                            ...params.inputProps,
                            title: labelText || undefined,
                          }}
                          {...GetReadOnlyInputProps(params)}
                          variant="filled"
                          label="View Transactions"
                      onKeyDown={(e) => {
                        if (e.key === "Tab" && highlightedViewTransaction) {
                          handleTransactions(e, highlightedViewTransaction);
                          setViewTransactionOpen(false);
                        }
                      }}
                      sx={{
                        "& .MuiFilledInput-root": {
                          height: "35px",
                          minHeight: "35px",
                          paddingTop: "12px",
                        },
                        "& .MuiInputLabel-root": {
                          transform: "translate(12px, 10px) scale(1)",
                          "&.MuiInputLabel-shrink": {
                            transform: "translate(12px, 4px) scale(0.75)",
                          },
                        },
                      }}
                    />
                  </span>
                );
              }}
                />

                <CustomeDateRange
                  startDate={fromDate}
                  endDate={toDate}
                  onStartChange={(newValue) => {
                    setFromDate(newValue);
                    const rangeOption =
                      VIEW_TRANSACTION_OPTIONS.find((opt) => opt.value === "3" || opt.label === "Range") ||
                      defaultDateRange;
                    setViewTransactionInput("Range");
                    setViewTransaction(rangeOption);
                    setPage(0);
                  }}
                  onEndChange={(newValue) => {
                    setToDate(newValue);
                    const rangeOption =
                      VIEW_TRANSACTION_OPTIONS.find((opt) => opt.value === "3" || opt.label === "Range") ||
                      defaultDateRange;
                    setViewTransactionInput("Range");
                    setViewTransaction(rangeOption);
                    setPage(0);
                  }}
                />
              </Stack>

              <Stack
                direction={"row"}
                justifyContent={"end"}
                alignItems={"center"}
                gap={2}
              >
                <Autocomplete
                  options={["--All--", "Completed", "Attention Needed", "Failed"]}
                  size="small"
                  disableClearable
                  autoHighlight
                  selectOnFocus
                  open={statusOpen}
                  onOpen={() => setStatusOpen(true)}
                  onClose={() => setStatusOpen(false)}
                  value={status}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      setStatus(newValue);
                      setPage(0);
                    }
                  }}
                  onHighlightChange={(event, option) => setHighlightedStatus(option)}
                  ListboxProps={{
                    sx: {
                      maxHeight: 200,
                      "& .MuiAutocomplete-option": {
                        fontSize: 12,
                        borderBottom: "1px solid #e0e0e0",
                      },
                      "& .MuiAutocomplete-option:last-child": {
                        borderBottom: "none",
                      },
                      "& .MuiAutocomplete-option[aria-selected='true']": {
                        backgroundColor: "#2f5d8a !important",
                        color: "white",
                      },
                    },
                  }}
                  sx={{ minWidth: "170px" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...GetReadOnlyInputProps(params)}
                      variant="filled"
                      label="Status"
                      onKeyDown={(e) => {
                        if (e.key === "Tab" && highlightedStatus) {
                          setStatus(highlightedStatus);
                          setPage(0);
                          setStatusOpen(false);
                        }
                      }}
                      sx={{
                        "& .MuiFilledInput-root": {
                          height: "35px",
                          minHeight: "35px",
                          paddingTop: "11px",
                        },
                        "& .MuiInputLabel-root": {
                          transform: "translate(12px, 10px) scale(1)",
                          "&.MuiInputLabel-shrink": {
                            transform: "translate(12px, 4px) scale(0.75)",
                          },
                        },
                      }}
                    />
                  )}
                />
                <Button
                  disableRipple
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.06)",
                    border: "1px solid #d1d1d1",
                    borderRadius: "4px",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontWeight: 400,
                    fontSize: "14px",
                    height: "35px",
                    px: 2.5,
                  }}
                  startIcon={<FilterList sx={{ color: "rgba(0, 0, 0, 0.54)" }} />}
                  onClick={(e) => setShowSearch(true)}
                >
                  Filter
                </Button>
                <Button
                  disableRipple
                  sx={{
                    borderRadius: "4px",
                    fontWeight: 500,
                    fontSize: "12px",
                    height: "35px",
                    px: 2,
                  }}
                  startIcon={<Add sx={{ color: "black" }} />}
                  onClick={() => {
                    if (!permissions.canCreate) {
                      showErrorSnackbar("You don't have permission to add new payroll.");
                      return;
                    }
                    setEditRow({
                      id: null,
                      corporationId: corpId,
                    })
                    setIsAddPayRoll(true)
                  }
                  }
                >
                  ADD NEW PAYROLL
                </Button>

              </Stack>
            </Stack>

            <Stack
              flexGrow={1}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                <PayrollEntryListTable
                  rows={rows}
                  columns={columns}
                  setEdit={(row) => {
                    if (!permissions.canUpdate) {
                      showErrorSnackbar("You don't have permission to edit payroll entries.");
                      return;
                    }
                    // if (row.raw?.IsActive === "Attention Needed" && row.raw?.ImportPayrollType === 2) {
                    if (row.raw?.IsAgentLable && row.raw?.ImportPayrollType === 2) {
                      setItemMappingRow(row);
                    } else {
                      setEditRow(row);
                    }
                  }}
                  onDelete={(row) => {
                    if (!permissions.canDelete) {
                      showErrorSnackbar("You don't have permission to delete payroll entries.");
                      return;
                    }
                    handleDelete(row);
                  }}
                  loading={loading}
                />
              </Box>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{
                  height: "30px",
                  background:
                    "linear-gradient(to bottom, #e6e6e6 0%, #f2f2f2 50%, #e6e6e6 100%)",
                  borderTop: "1px solid #cfcfcf",
                  borderBottom: "1px solid #cfcfcf",
                }}
              >
                {![101, 102, 103].includes(sortBy) && (
                  <CustomPagination
                    count={totalPages}
                    page={page}
                    onChange={(e, newPage) => setPage(newPage - 1)}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Box>

        {Boolean(showSearch) && (
          <SearchFilters
            open={showSearch}
            onClose={() => setShowSearch(false)}
            // hiddenFields={isSortView ? ["SortBy"] : []}
            onApply={(
              filters,
              order,
              selectedSortBy,
              selectedCorporationValue,
            ) => {
              setSearchFilters(filters);
              const newSortBy = Number(selectedSortBy) || 107;
              if (newSortBy !== sortBy || order !== sortOrder) {
                if (defaultDateRange) {
                  setViewTransaction(defaultDateRange);
                  setViewTransactionInput(defaultDateRange.label || defaultDateRange.date || "Range");
                }
              }
              setSortBy(newSortBy);
              setSortOrder(order);
              const matchedCorporation = corporations.find(
                (corp) =>
                  String(corp.corpValue ?? "").toLowerCase() ===
                  String(selectedCorporationValue ?? "").toLowerCase(),
              );
              if (matchedCorporation) {
                setSelectedCorp(matchedCorporation);
              }
              setShowSearch(false);
              setPage(0);
            }}
            initialSortOrder={sortOrder}
            initialSortBy={sortBy}
            initialFilters={searchFilters}
            corporationOptions={corporations}
            initialCorporation={selectedCorp}
          />
        )}
        {Boolean(editRow) && (
          <PayPeriodSetup
            open={Boolean(editRow)}
            onClose={() => {
              setEditRow(null);
              setRefreshKey((prev) => prev + 1);
              setIsAddPayRoll(false);
            }}
            payrollId={editRow?.id}
            corpId={editRow?.corporationId}
            rowData={editRow}
            isAddPayRoll={isAddPayRoll}
            corporationList={corporations}
          />
        )}
        {Boolean(itemMappingRow) && (
          <PayrollItemTypeMapping
            open={Boolean(itemMappingRow)}
            onClose={() => {
              setItemMappingRow(null);
            }}
            onUpdateAndNext={(row) => {
              setItemMappingRow(null);
              setEditRow(row);
            }}
            rowData={itemMappingRow}
            source="PayrollEntry"
          />
        )}

        <PayrollsPopup
          open={popup.open}
          popupType={popup.type}
          message={popup.message}
          onConfirm={popup.onConfirm}
          onCancel={hidePopup}
        />

      </>
    </SnackbarProvider>
  );
};

export default PayrollEntry;
